import type { HazardOptionStateByHazard } from '@app/components/Molecules/RiskSideDrawerContent/tabs/InformativePanel/LayersTab/HazardControls'
import type { RiskWorkspace } from '@app/types/workspaceTypes'
import { atom } from 'jotai'
import type { Map } from 'mapbox-gl'
import { APP_INITIAL_LOCATION, BASE_MAP_LIGHT_WITH_HOUSE_NUMBERS } from '@app/app-constants'
import type { MutableRefObject } from 'react'
import type { MapLayer, MapLocation, SimplifiedGeoJSONFeature } from '@app/types/layerTypes'

export const riskMapInstanceAtom = atom<Map | null>(null)

export const hazardOptionStateAtom = atom<HazardOptionStateByHazard>({})

export const prevHazardOptionStateAtom = atom<HazardOptionStateByHazard | null>(null)

export const showAddLayersModalTabAtom = atom('')

export const cascadingAssetNDaysAtom = atom<Record<string, number | null>>({})

type WorkspaceSet = RiskWorkspace['scenario_sets'][number] | RiskWorkspace['capital_sets'][number]

export type WorkspaceSetOption = { index: number; name: string }

// Options keep the set's index in the workspace, so duplicate names stay distinct
const getSetOptions = (sets: WorkspaceSet[] = []): WorkspaceSetOption[] =>
  sets.flatMap((set, index) => ('name' in set ? [{ index, name: set.name }] : []))

export const getScenarioSetOptions = (workspace: RiskWorkspace | null) =>
  getSetOptions(workspace?.scenario_sets)

const HIDDEN_CAPITAL_SET_NAMES = ['social', 'economic']

// Social/Economic presets are never shown or loaded on the map
export const getCapitalSetOptions = (workspace: RiskWorkspace | null) =>
  getSetOptions(workspace?.capital_sets).filter(
    ({ name }) => !HIDDEN_CAPITAL_SET_NAMES.includes(name.trim().toLowerCase()),
  )

// Falls back to the first set when nothing (or a stale index) is selected
export const resolveSetIndex = (options: WorkspaceSetOption[], selected: number | null) =>
  options.some(({ index }) => index === selected) ? selected : (options[0]?.index ?? null)

const selectedScenarioSetIndexAtom = atom<number | null>(null)
const selectedCapitalSetIndexAtom = atom<number | null>(null)

// Layer ids the active workspace put on the map, so set switches only swap those
export const activeWorkspaceLayerIdsAtom = atom<RiskWorkspace['layers'] | null>(null)

const baseActiveWorkspaceAtom = atom<RiskWorkspace | null>(null)

// Changing workspace resets the set selection
export const activeWorkspaceAtom = atom(
  (get) => get(baseActiveWorkspaceAtom),
  (_get, set, workspace: RiskWorkspace | null) => {
    set(baseActiveWorkspaceAtom, workspace)
    set(selectedScenarioSetIndexAtom, null)
    set(selectedCapitalSetIndexAtom, null)
    set(activeWorkspaceLayerIdsAtom, null)
  },
)

export const activeScenarioSetIndexAtom = atom(
  (get) =>
    resolveSetIndex(
      getScenarioSetOptions(get(activeWorkspaceAtom)),
      get(selectedScenarioSetIndexAtom),
    ),
  (_get, set, index: number) => set(selectedScenarioSetIndexAtom, index),
)

export const activeCapitalSetIndexAtom = atom(
  (get) =>
    resolveSetIndex(
      getCapitalSetOptions(get(activeWorkspaceAtom)),
      get(selectedCapitalSetIndexAtom),
    ),
  (_get, set, index: number) => set(selectedCapitalSetIndexAtom, index),
)

export const riskMapInteractiveAtom = atom(true)

export const riskMapIsFullScreenAtom = atom(false)

export const riskBasemapAtom = atom('Street')

export const riskMapIs3DAtom = atom(false)

export const riskMapStyleAtom = atom(BASE_MAP_LIGHT_WITH_HOUSE_NUMBERS)

export const flyToClientLocationAtom = atom(true)

export const clientLocationAtom = atom<MapLocation>(APP_INITIAL_LOCATION)

export const clientHasChangedAtom = atom(false)

export const selectedMarkerAtom = atom<MapLocation | null>(null)

export const selectedFeatureAtom = atom<SimplifiedGeoJSONFeature | null>(null)

export const selectedLayerAtom = atom<MapLayer | null>(null)

export const mapPaddingIsChangingAtom = atom({
  top: false,
  right: false,
  bottom: false,
  left: false,
})
export type RiskMapPadding = {
  top: number
  right: number
  bottom: number
  left: number
}

export const riskMapPaddingRefAtom = atom<MutableRefObject<RiskMapPadding>>({
  current: {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
})
